import * as React from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "./button"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "./command"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "./popover"
import { Badge } from "./badge"

/**
 * MultiSelect Component
 * 
 * @param {Array} options - Array of objects { label, value }
 * @param {Array} selected - Array of selected values
 * @param {Function} onChange - Callback function(values)
 * @param {String} placeholder - Placeholder text
 * @param {String} emptyText - Text to show when no results found
 * @param {String} className - Additional classes
 */
export function MultiSelect({
    options = [],
    selected = [],
    onChange,
    placeholder = "Select options...",
    emptyText = "No results found.",
    className,
    ...props
}) {
    const [open, setOpen] = React.useState(false)

    const handleUnselect = (item) => {
        onChange(selected.filter((i) => i !== item))
    }

    return (
        <Popover open={open} onOpenChange={setOpen} {...props}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        "w-full justify-between h-auto min-h-10",
                        selected.length > 0 ? "h-auto" : "h-10",
                        className
                    )}
                >
                    <div className="flex flex-wrap gap-1 max-h-12 overflow-y-auto w-full pr-1">
                        {selected.length === 0 && (
                            <span className="text-muted-foreground font-normal">{placeholder}</span>
                        )}
                        {selected.map((item) => {
                            const option = options.find((opt) => opt.value === item)
                            return (
                                <Badge
                                    key={item}
                                    variant="secondary"
                                    className="mr-1 mb-1"
                                >
                                    {option ? option.label : item}
                                    <div
                                        className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleUnselect(item)
                                            }
                                        }}
                                        onMouseDown={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                        }}
                                        onClick={() => handleUnselect(item)}
                                    >
                                        <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                                    </div>
                                </Badge>
                            )
                        })}
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
                <Command>
                    <CommandInput placeholder="Search..." />
                    <CommandList>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup className="max-h-18 overflow-auto">
                            {options.map((option) => (
                                <CommandItem
                                    key={option.value}
                                    value={`${option.label}\t${option.value}`}
                                    onSelect={() => {
                                        const isSelected = selected.includes(option.value)
                                        if (isSelected) {
                                            onChange(selected.filter((item) => item !== option.value))
                                        } else {
                                            onChange([...selected, option.value])
                                        }
                                    }}
                                >
                                    <Check
                                        className={cn(
                                            "mr-2 h-4 w-4",
                                            selected.includes(option.value) ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    {option.label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
